<script setup lang="ts">
  import { SessionStore } from '~/stores/session'
  import type { StateMachineContext } from '~/models/StateMachineModels'
  import { ProfileStore } from '~/stores/profile'
  import type { PushToSaveScenarios } from '~/composables/PushToSave'

  const props = defineProps<{
    context: StateMachineContext
    presentation: string
  }>()

  const session = SessionStore.use()
  const profile = ProfileStore.use()

  const scenario = computed(() => (props.context?.config ?? 'add-needs') as PushToSaveScenarios)
  const { data } = await PushToSaveContent.use(scenario.value)

  const screen = computed(() => data.value?.screens?.phoneCapture)

  const { phoneValidation } = AccountValidation.use()
  const { consentText } = await ContactConsent.use(
    'By providing my phone number and clicking "Submit",'
  )

  const { track } = Analytics.use()

  const loading = ref(false)

  const displayCustomerEmail = computed(() => session.authBag.identifier?.replace('@', '<wbr />@'))

  const onSubmit = async () => {
    loading.value = true

    try {
      await ContactConsent.saveConsents(['tcpa', 'ptc'], consentText.value)

      const { createContactPhoneClient } = PxApi.use()
      const client = createContactPhoneClient()
      await client.saveContactPhone({
        profileId: session.profileId!,
        number: session.authBag.accountPhone,
        isMobileVerified: false
      })

      await profile.load()

      track('PushToSavePhoneAdded', {
        scenario: _camelCase(scenario.value),
        type: 'two-part-flow'
      })
    } catch (e) {
      console.error('Failed to save phone/consents: ', e)
    }

    loading.value = false
    props.context.next()
  }

  const onSkip = () => {
    track('PushToSavePhoneSkipped', {
      scenario: _camelCase(scenario.value),
      type: 'two-part-flow'
    })
    props.context.next()
  }
</script>

<template>
  <aside
    class="mx-auto mb-5 grid w-fit place-items-center gap-1 rounded-xl border border-success/20 bg-[#e6f7df] p-2 px-5 text-base font-semibold leading-tight text-success"
  >
    <span class="items-center text-center">
      <HpIcon name="lucide:check" class="inline" />
      {{ screen?.confirmation }}
      <strong class="text-success-dark" v-html="displayCustomerEmail"></strong>
    </span>
  </aside>

  <div class="mb-7 grid place-items-center gap-3 text-center">
    <h2>
      <span class="block text-5xl leading-none">{{ screen?.icon }}</span>
      {{ screen?.heading }}
    </h2>
    <p class="text-balance text-lg font-normal leading-snug text-primary-900">
      {{ screen?.text }}
    </p>
  </div>

  <HpForm
    id="phone-capture-form"
    :validation-schema="phoneValidation"
    :on-submit="onSubmit"
    class="flex w-full flex-col items-center gap-y-3"
  >
    <HpFormMaskedInput
      id="account-phone"
      mask="phone"
      type="tel"
      :label="data?.labels?.phone"
      :placeholder="data?.labels?.phone"
      v-model="session.authBag.accountPhone"
      class="w-full"
    />

    <PxTcpaConsentCheckbox
      class="max-h-28 text-left [&_small]:text-sm [&_small]:leading-normal"
      :text="consentText"
      scroll-agreement
    />

    <HpButton class="mt-3 w-full max-w-xs" :busy="loading">Submit</HpButton>
  </HpForm>

  <HpAnchor @click.prevent="onSkip" class="mt-5 block text-center text-lg font-semibold underline">
    No thanks
  </HpAnchor>
</template>

<style scoped></style>
